import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import Breadcrumb from "@/components/ui/breadcrumb";
import Dropdown from "@/components/ui/dropdown";
import Button from "@/components/ui/button";
import Input from "@/components/ui/input";
import Textarea from "@/components/ui/textarea";
import {
  LeftChevron,
  ArrowLeft,
  ArrowRight,
  Check,
  FileUpload,
  Delete,
} from "@/components/icons";
import ConfirmModal from "@/components/ui/confirm-modal";
import { renderPaginationNumbers } from "@/utils/pagination";
import { uploadAdminFiles } from "@/lib/files.api";
import {
  CertificateTemplate,
  mapApiTemplateToCertificateTemplate,
} from "../config/certificate-templates";
import {
  certificatesApi,
  CertificateOwnerType,
} from "../services/certificates.api";
import {
  CertificateData,
  DEMO_CERTIFICATE_DATA,
  generateAndDownloadCertificatePDF,
} from "../utils/certificate-pdf-generator";
import CertificatePreview, {
  CERTIFICATE_FIELD_KEYS,
  type CertificateFieldKey,
} from "../components/CertificatePreview";
import CertificatePositionEditor from "../components/CertificatePositionEditor";
import PlaceholderChips, {
  Placeholder,
} from "../components/PlaceholderChips";
import styles from "./CreateCertificatePage.module.scss";

const DEFAULT_NUMBER_PATTERN = "{YEAR}{MONTH}{DAY}-{SEQ:4}";

/** {{educationName}} -> educationTitle gibi ters eşleme (kayıtlı HTML'i okurken) */
const PLACEHOLDER_TO_FIELD = Object.fromEntries(
  Object.entries(CERTIFICATE_FIELD_KEYS).map(([field, placeholder]) => [
    placeholder,
    field,
  ]),
) as Record<string, keyof typeof CERTIFICATE_FIELD_KEYS>;

/**
 * Kayıtlı sertifika HTML'inden GİZLİ alanları geri okur.
 *
 * Gizleme ayrı bir alanda saklanmıyor: tasarımın kendisi `rawHtml` ve gizli
 * alan o HTML'e hiç yazılmıyor. Dolayısıyla kural basit — HTML'de karşılığı
 * olmayan alan gizlidir.
 *
 * Bu, gizleme eklenmeden önce kaydedilmiş sertifikalarda da doğru sonuç verir:
 * o kayıtlar altı alanın hepsini içerir (değeri boş olsa bile boş bir
 * `data-certificate-field` div'i yazılıyordu), yani hepsi görünür okunur.
 *
 * Bozuk/çözülemeyen HTML'de hiçbir alan gizlenmez — tasarımı sessizce
 * budamaktansa fazla göstermek tercih edilir.
 */
function parseHiddenFieldsFromRawHtml(rawHtml: string): CertificateFieldKey[] {
  try {
    const doc = new DOMParser().parseFromString(rawHtml, "text/html");
    const present = new Set<string>();
    doc.querySelectorAll("[data-certificate-field]").forEach((el) => {
      const placeholder = el.getAttribute("data-certificate-field");
      const field = placeholder ? PLACEHOLDER_TO_FIELD[placeholder] : undefined;
      if (field) present.add(field);
    });
    // Hiç işaretli alan yoksa HTML bu şemayla yazılmamıştır; gizleme çıkarımı
    // yapmak tüm alanları silmek olurdu.
    if (present.size === 0) return [];
    return (Object.keys(CERTIFICATE_FIELD_KEYS) as CertificateFieldKey[]).filter(
      (key) => !present.has(key),
    );
  } catch {
    return [];
  }
}

/**
 * Kayıtlı sertifika HTML'inden alan konumlarını geri okur.
 *
 * Kaydederken her alan `data-certificate-field` işaretli bir div'e sarılıyor ve
 * konum/tipografi bilgisi bu div'in EBEVEYNİNDE inline style olarak duruyor
 * (bkz. CertificatePreview.TextOverlay). Düzenlemede tasarımı sıfırdan
 * kurdurmamak için o stilleri ayrıştırıp editöre geri veriyoruz. Çözülemeyen
 * alan şablonun varsayılan konumunda kalır.
 */
function parsePositionsFromRawHtml(
  rawHtml: string,
  fallback: CertificateTemplate["textPositions"],
): CertificateTemplate["textPositions"] {
  const result = JSON.parse(
    JSON.stringify(fallback),
  ) as CertificateTemplate["textPositions"];
  try {
    const doc = new DOMParser().parseFromString(rawHtml, "text/html");
    doc.querySelectorAll("[data-certificate-field]").forEach((el) => {
      const placeholder = el.getAttribute("data-certificate-field");
      const field = placeholder ? PLACEHOLDER_TO_FIELD[placeholder] : undefined;
      const wrapper = el.parentElement as HTMLElement | null;
      if (!field || !wrapper || !result[field]) return;
      const style = wrapper.style;
      const num = (value: string) => {
        const n = parseFloat(value);
        return Number.isFinite(n) ? n : undefined;
      };
      const current = result[field];
      const x = num(style.left);
      const y = num(style.top);
      const fontSize = num(style.fontSize);
      const maxWidth = num(style.maxWidth);
      result[field] = {
        ...current,
        x: x ?? current.x,
        y: y ?? current.y,
        fontSize: fontSize ?? current.fontSize,
        fontFamily: style.fontFamily || current.fontFamily,
        fontWeight: style.fontWeight || current.fontWeight,
        color: style.color || current.color,
        textAlign:
          style.textAlign === "left" ||
          style.textAlign === "center" ||
          style.textAlign === "right"
            ? style.textAlign
            : current.textAlign,
        maxWidth: style.maxWidth.endsWith("%") ? maxWidth : current.maxWidth,
      };
    });
  } catch {
    // Bozuk HTML kaydı düzenlemeyi kilitlemesin: şablon varsayılanıyla açılır.
  }
  return result;
}

const buildDescriptionPlaceholders = (t: (k: string) => string): Placeholder[] => [
  { label: t("certificates.create.tokens.participantName"), token: "{PARTICIPANT_NAME}" },
  { label: t("certificates.create.tokens.educationName"), token: "{EDUCATION_NAME}" },
  { label: t("certificates.create.tokens.instructorName"), token: "{INSTRUCTOR_NAME}" },
  { label: t("certificates.create.tokens.issueDate"), token: "{ISSUE_DATE}" },
  { label: t("certificates.create.tokens.certificateNumber"), token: "{CERTIFICATE_NUMBER}" },
  { label: t("certificates.create.tokens.year"), token: "{YEAR}" },
];

const buildNumberPlaceholders = (t: (k: string) => string): Placeholder[] => [
  { label: t("certificates.create.tokens.year"), token: "{YEAR}" },
  { label: t("certificates.create.tokens.yearShort"), token: "{YEAR:2}" },
  { label: t("certificates.create.tokens.month"), token: "{MONTH}" },
  { label: t("certificates.create.tokens.day"), token: "{DAY}" },
  { label: t("certificates.create.tokens.seq4"), token: "{SEQ:4}" },
  { label: t("certificates.create.tokens.seq6"), token: "{SEQ:6}" },
];

function previewNumberPattern(pattern: string): string {
  const d = new Date();
  const year = String(d.getFullYear());
  return pattern
    .replace(/\{YEAR:2\}/g, year.slice(-2))
    .replace(/\{YEAR\}/g, year)
    .replace(/\{MONTH\}/g, String(d.getMonth() + 1).padStart(2, "0"))
    .replace(/\{DAY\}/g, String(d.getDate()).padStart(2, "0"))
    .replace(/\{DOMAIN\}/g, "DOMAIN-UUID")
    .replace(/\{SEQ:(\d{1,2})\}/g, (_m, n) => {
      const width = Math.min(12, Math.max(1, Number(n) || 1));
      return "42".padStart(width, "0");
    });
}

function validateNumberPattern(
  pattern: string,
  t: (k: string) => string,
): string | null {
  const trimmed = pattern.trim();
  if (!trimmed) return null;
  if (trimmed.length > 200) return "Pattern en fazla 200 karakter olabilir.";
  const allowed = new Set(["YEAR", "YEAR:2", "MONTH", "DAY", "DOMAIN"]);
  const tokens = trimmed.match(/\{([^}]+)\}/g) ?? [];
  for (const raw of tokens) {
    const content = raw.slice(1, -1);
    if (allowed.has(content)) continue;
    const seqMatch = /^SEQ:(\d{1,2})$/.exec(content);
    if (seqMatch) {
      const n = Number(seqMatch[1]);
      if (n < 1 || n > 12)
        return `{SEQ:${seqMatch[1]}} geçersiz. 1–12 arası olmalı.`;
      continue;
    }
    return `Bilinmeyen token: {${content}}. Desteklenenler: {YEAR}, {YEAR:2}, {MONTH}, {DAY}, {SEQ:N}.`;
  }
  // Token dışı karakter seti kısıtı: alfanumerik, tire, alt çizgi, slash, nokta, boşluk.
  // Aksi halde "saçma sapan" metin numara formatı olarak kabul ediliyordu.
  const withoutTokens = trimmed.replace(/\{[^}]+\}/g, "");
  if (withoutTokens && !/^[A-Za-z0-9 _\-/.]+$/.test(withoutTokens)) {
    return t("certificates.create.errors.numberFormatChars");
  }
  // Tek başına serbest metin de kabul edilmesin — en az bir bilinen token gerekli.
  if (tokens.length === 0) {
    return t("certificates.create.errors.numberFormatToken");
  }
  return null;
}

const TEMPLATE_FILE_ACCEPT = "image/jpeg,image/png,image/webp,image/gif";
const PAGE_SIZE_OPTIONS = [6, 12, 18];

type WizardStep = 0 | 1 | 2 | 3;

const CreateCertificatePage: React.FC = () => {
  const { t } = useTranslation();
  const DESCRIPTION_PLACEHOLDERS = useMemo(() => buildDescriptionPlaceholders(t), [t]);
  const NUMBER_PLACEHOLDERS = useMemo(() => buildNumberPlaceholders(t), [t]);
  const navigate = useNavigate();
  // Route'ta :certificateId varsa düzenleme modundayız (/certificates/:id/edit).
  const { certificateId } = useParams<{ certificateId: string }>();
  // Sertifikalar ekranındaki Stajyer sekmesi boşken "Staj Sertifikası Tasarla"
  // ile buraya `?ownerType=1` göndererek geliyor; tür önceden seçili açılsın.
  const [searchParams] = useSearchParams();
  const isEditMode = !!certificateId;
  const previewRef = useRef<HTMLDivElement>(null);
  const livePreviewRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<WizardStep>(0);
  const [selectedTemplate, setSelectedTemplate] =
    useState<CertificateTemplate | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(6);
  const [isGenerating, setIsGenerating] = useState(false);
  const [templates, setTemplates] = useState<CertificateTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [templatesError, setTemplatesError] = useState<string | null>(null);
  const [uploadingTemplate, setUploadingTemplate] = useState(false);
  const [savingCertificate, setSavingCertificate] = useState(false);
  const [loadingCertificate, setLoadingCertificate] = useState(false);
  /** Düzenlemede detay yüklendi mi — şablon listesi geldikten sonra eşleştiriyoruz */
  const [editPrefillDone, setEditPrefillDone] = useState(false);
  /** Kayıtlı HTML'den çözülen konumlar; şablon set edildikten SONRA uygulanır */
  const [pendingPositions, setPendingPositions] = useState<
    CertificateTemplate["textPositions"] | null
  >(null);
  const [certificateName, setCertificateName] = useState("");
  const [certificateDescription, setCertificateDescription] = useState("");
  const [numberPattern, setNumberPattern] = useState("");
  // Varsayılan eğitim: sertifikaların ezici çoğunluğu eğitim sertifikası ve
  // staj sertifikası domain başına yalnızca bir kez tanımlanıyor. URL'de tür
  // verilmişse (Stajyer sekmesinin boş-durum butonu) ondan başlanır.
  const [ownerType, setOwnerType] = useState<CertificateOwnerType>(() =>
    Number(searchParams.get("ownerType")) === CertificateOwnerType.INTERN
      ? CertificateOwnerType.INTERN
      : CertificateOwnerType.EDUCATION,
  );
  const [templateToDelete, setTemplateToDelete] =
    useState<CertificateTemplate | null>(null);
  const [deletingTemplate, setDeletingTemplate] = useState(false);
  const [customPositions, setCustomPositions] = useState<
    CertificateTemplate["textPositions"] | null
  >(null);
  /** Sertifikaya hiç yazılmayacak alanlar ("bizim sertifikamızda eğitmen yok"). */
  const [hiddenFields, setHiddenFields] = useState<CertificateFieldKey[]>([]);
  /** Düzenlemede kayıtlı HTML'den çözülen gizli alanlar; şablonla birlikte uygulanır. */
  const [pendingHiddenFields, setPendingHiddenFields] = useState<
    CertificateFieldKey[] | null
  >(null);

  // Yeni şablon seçildiğinde positions'ı sıfırla (şablonun varsayılanını kullanacak).
  useEffect(() => {
    if (!selectedTemplate) {
      setCustomPositions(null);
      setHiddenFields([]);
      return;
    }
    setCustomPositions(
      JSON.parse(JSON.stringify(selectedTemplate.textPositions)),
    );
    // Şablon değişince alan seçimi de varsayılana döner: yeni şablonun tasarımı
    // gizlenen alanı yeniden anlamlı kılıyor olabilir.
    setHiddenFields([]);
  }, [selectedTemplate?.id]);

  /**
   * Düzenlemede kayıtlı konumları geri yükler.
   *
   * Yukarıdaki efekt şablon değişince konumları varsayılana çekiyor; bu efekt
   * ondan sonra tanımlı olduğu için aynı render'da en son çalışır ve kayıtlı
   * tasarımın konumları korunur.
   */
  useEffect(() => {
    if (!pendingPositions || !selectedTemplate) return;
    setCustomPositions(pendingPositions);
    setPendingPositions(null);
  }, [pendingPositions, selectedTemplate?.id]);

  /** Düzenlemede kayıtlı alan seçimini geri yükler (konumlarla aynı gerekçe). */
  useEffect(() => {
    if (!pendingHiddenFields || !selectedTemplate) return;
    setHiddenFields(pendingHiddenFields);
    setPendingHiddenFields(null);
  }, [pendingHiddenFields, selectedTemplate?.id]);

  /** Kullanıcı konumlandırdıysa onunla, değilse şablon defaultlarıyla birleşik template. */
  const effectiveTemplate: CertificateTemplate | null = useMemo(() => {
    if (!selectedTemplate) return null;
    if (!customPositions) return selectedTemplate;
    return { ...selectedTemplate, textPositions: customPositions };
  }, [selectedTemplate, customPositions]);

  const numberPatternError = useMemo(
    () => validateNumberPattern(numberPattern, t),
    [numberPattern],
  );
  const effectivePattern =
    numberPattern.trim() === "" ? DEFAULT_NUMBER_PATTERN : numberPattern.trim();
  const numberPreview = useMemo(
    () => previewNumberPattern(effectivePattern),
    [effectivePattern],
  );

  /**
   * Önizlemede gösterilecek veri: gerçek kayıt sırasında backend doldurur, burada
   * sadece şablonun nasıl görüneceğini göstermek için demo veriyi kullanıcının
   * yazdığı açıklama metni ile birleştiriyoruz.
   */
  const previewData: CertificateData = useMemo(
    () => ({
      ...DEMO_CERTIFICATE_DATA,
      description: certificateDescription || DEMO_CERTIFICATE_DATA.description,
      certificateNumber: numberPreview,
    }),
    [certificateDescription, numberPreview],
  );

  const insertIntoDescription = (token: string) => {
    setCertificateDescription((prev) =>
      prev.length > 0 && !prev.endsWith(" ") ? `${prev} ${token}` : prev + token,
    );
  };

  const insertIntoNumberPattern = (token: string) => {
    setNumberPattern((prev) => prev + token);
  };

  const fetchTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    setTemplatesError(null);
    try {
      const apiTemplates =
        await certificatesApi.getEducationCertificateTemplates();
      setTemplates(
        apiTemplates.map((api, index) =>
          mapApiTemplateToCertificateTemplate(api, index),
        ),
      );
    } catch (err) {
      setTemplatesError(
        err instanceof Error ? err.message : t("certificates.create.errors.templateLoadFailed"),
      );
      setTemplates([]);
    } finally {
      setTemplatesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  /**
   * Düzenleme modu: mevcut sertifikayı forma doldurur.
   *
   * Şablon listesi gelmeden çalışamaz — kayıtlı `certificateTemplateID`'yi
   * listeden eşleştirip seçili hale getiriyoruz; şablon silinmişse kullanıcı
   * ilk adımda yeni bir şablon seçer.
   */
  useEffect(() => {
    if (!isEditMode || !certificateId || editPrefillDone || templatesLoading)
      return;
    let cancelled = false;
    setLoadingCertificate(true);
    certificatesApi
      .getCertificateDetail(certificateId)
      .then((detail) => {
        if (cancelled) return;
        if (!detail) {
          toast.error(t("certificates.edit.loadError"));
          return;
        }
        setCertificateName(detail.name ?? "");
        setCertificateDescription(detail.description ?? "");
        setNumberPattern(detail.certificateNumberPattern ?? "");
        if (detail.ownerType !== undefined) setOwnerType(detail.ownerType);
        const tpl =
          templates.find((x) => x.id === detail.certificateTemplateID) ?? null;
        if (tpl) {
          setSelectedTemplate(tpl);
          if (detail.rawHtml) {
            setPendingPositions(
              parsePositionsFromRawHtml(detail.rawHtml, tpl.textPositions),
            );
            setPendingHiddenFields(parseHiddenFieldsFromRawHtml(detail.rawHtml));
          }
          setStep(1);
        } else {
          toast.error(t("certificates.edit.templateMissing"));
        }
        setEditPrefillDone(true);
      })
      .catch(() => {
        if (!cancelled) toast.error(t("certificates.edit.loadError"));
      })
      .finally(() => {
        if (!cancelled) setLoadingCertificate(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isEditMode, certificateId, editPrefillDone, templatesLoading, templates]);

  const handleGoBack = () => navigate("/dashboard/certificates");

  const breadcrumbItems = [
    {
      label: t("certificates.title"),
      onClick: () => navigate("/dashboard/certificates"),
    },
    {
      label: isEditMode
        ? t("certificates.edit.title")
        : t("certificates.create.title"),
    },
  ];

  const totalRecords = templates.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));

  const paginatedTemplates = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return templates.slice(start, start + pageSize);
  }, [templates, currentPage, pageSize]);

  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value));
    setCurrentPage(1);
  };

  const handleTemplateSelect = (template: CertificateTemplate) => {
    setSelectedTemplate(
      selectedTemplate?.id === template.id ? null : template,
    );
  };

  const handleUploadTemplateClick = () => fileInputRef.current?.click();

  const handleTemplateFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    setUploadingTemplate(true);
    try {
      const [uploaded] = await uploadAdminFiles([file]);
      const fileId = uploaded?.id;
      if (!fileId) throw new Error(t("certificates.create.errors.fileIdMissing"));
      await certificatesApi.createEducationCertificateTemplate(fileId);
      toast.success(
        t("certificates.create.uploadSuccess"),
      );
      await fetchTemplates();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : t("certificates.create.errors.templateUploadFailed"),
      );
    } finally {
      setUploadingTemplate(false);
    }
  };

  const handleRequestDeleteTemplate = (
    template: CertificateTemplate,
    e: React.MouseEvent,
  ) => {
    e.stopPropagation();
    setTemplateToDelete(template);
  };

  const handleConfirmDeleteTemplate = async () => {
    if (!templateToDelete) return;
    setDeletingTemplate(true);
    try {
      await certificatesApi.deleteEducationCertificateTemplate(
        templateToDelete.id,
      );
      toast.success(
        t("certificates.create.deleteSuccess"),
      );
      if (selectedTemplate?.id === templateToDelete.id) {
        setSelectedTemplate(null);
      }
      setTemplateToDelete(null);
      await fetchTemplates();
    } catch (err: unknown) {
      const statusCode = (err as { response?: { status?: number } })?.response
        ?.status;
      const errorPayload = (
        err as {
          response?: { data?: { code?: string; errorCode?: string } };
        }
      )?.response?.data;
      const code = errorPayload?.code ?? errorPayload?.errorCode;
      const isConflict =
        statusCode === 409 ||
        code === "EDUCATION_CERTIFICATE_TEMPLATE_HAS_ACTIVE_CERTIFICATES";
      const isNotFound =
        statusCode === 404 ||
        code === "EDUCATION_CERTIFICATE_TEMPLATE_NOT_FOUND";
      if (isConflict) {
        toast.error(
          t("certificates.create.deleteConflict"),
        );
      } else if (isNotFound) {
        toast.error(
          t("certificates.create.deleteNotFound"),
        );
        // 404 → zaten yok, modal'ı kapat ve listeyi tazele
        setTemplateToDelete(null);
        await fetchTemplates();
      } else {
        toast.error(
          err instanceof Error ? err.message : t("certificates.create.errors.templateDeleteFailed"),
        );
      }
    } finally {
      setDeletingTemplate(false);
    }
  };

  const handleGeneratePdf = async () => {
    if (!previewRef.current || !selectedTemplate) return;
    setIsGenerating(true);
    try {
      const filename = `certificate-${certificateName || "template"}-${Date.now()}`;
      await generateAndDownloadCertificatePDF(previewRef.current, filename);
    } catch (error) {
      console.error("Error generating PDF:", error);
    } finally {
      setIsGenerating(false);
    }
  };

  // Dropdown string taşıdığı için enum'a geri parse ediliyor.
  const ownerTypeOptions = [
    {
      value: String(CertificateOwnerType.EDUCATION),
      label: t("certificates.create.fields.ownerTypeEducation"),
    },
    {
      value: String(CertificateOwnerType.INTERN),
      label: t("certificates.create.fields.ownerTypeIntern"),
    },
  ];

  const handleOwnerTypeSelect = (value: string | string[]) => {
    const v = Array.isArray(value) ? value[0] : value;
    setOwnerType(
      Number(v) === CertificateOwnerType.INTERN
        ? CertificateOwnerType.INTERN
        : CertificateOwnerType.EDUCATION,
    );
  };

  const handleSave = async () => {
    if (!previewRef.current || !selectedTemplate) return;
    if (!certificateName.trim()) {
      toast.error(
        t("certificates.create.form.requiredCertificateName"),
      );
      setStep(1);
      return;
    }
    if (numberPatternError) {
      toast.error(numberPatternError);
      setStep(1);
      return;
    }
    const clone = previewRef.current.cloneNode(true) as HTMLElement;
    clone.querySelectorAll("[data-certificate-field]").forEach((el) => {
      const key = el.getAttribute("data-certificate-field");
      if (key) el.textContent = `{{${key}}}`;
    });
    const bgImage = clone.querySelector("img");
    if (bgImage) bgImage.setAttribute("src", "{{bgImage}}");

    setSavingCertificate(true);
    try {
      if (isEditMode && certificateId) {
        // Tür (ownerType) değişmez: eğitim <-> stajyer geçişi mevcut kayıtların
        // hangi motordan üretileceğini değiştirirdi.
        await certificatesApi.updateCertificate(certificateId, {
          certificateTemplateID: selectedTemplate.id,
          rawHtml: clone.outerHTML,
          name: certificateName.trim() || "Sertifika",
          description: certificateDescription.trim() || "",
          certificateNumberPattern:
            numberPattern.trim() === "" ? null : numberPattern.trim(),
        });
      } else {
        await certificatesApi.issueCertificate({
          templateId: selectedTemplate.id,
          rawHtml: clone.outerHTML,
          name: certificateName.trim() || "Sertifika",
          description: certificateDescription.trim() || "",
          certificateNumberPattern:
            numberPattern.trim() === "" ? null : numberPattern.trim(),
          ownerType,
        });
      }
      toast.success(
        isEditMode
          ? t("certificates.edit.saveSuccess")
          : t("certificates.create.saveSuccess"),
      );
      // Kaydet sonrası kullanıcı aynı sayfada kalıyor + formu boş görünce
      // "kaydoldu mu olmadı mı" diye takılıyordu — listeye yönlendiriyoruz.
      navigate("/dashboard/certificates");
    } catch (err) {
      // Backend aynı isimde sertifika için 409 döndürüyor; mesajı aynen göster.
      const axiosMsg = (err as { response?: { data?: { message?: string } } })
        ?.response?.data?.message;
      toast.error(
        axiosMsg ||
          (err instanceof Error ? err.message : "Sertifika kaydedilemedi"),
      );
    } finally {
      setSavingCertificate(false);
    }
  };

  const steps = [
    {
      label: t("certificates.create.steps.template"),
      description: t("certificates.create.steps.templateDesc"),
    },
    {
      label: t("certificates.create.steps.info"),
      description: t("certificates.create.steps.infoDesc"),
    },
    {
      label: t("certificates.create.steps.positioning"),
      description: t("certificates.create.steps.positioningDesc"),
    },
    {
      label: t("certificates.create.steps.preview"),
      description: t("certificates.create.steps.previewDesc"),
    },
  ];

  const missingForStep1 = [
    !certificateName.trim()
      ? t("certificates.create.form.certificateName")
      : null,
    numberPatternError ? t("certificates.create.errors.numberPatternInvalid") : null,
  ].filter(Boolean) as string[];

  const canGoNext =
    (step === 0 && !!selectedTemplate) ||
    (step === 1 && missingForStep1.length === 0) ||
    (step === 2 && !!selectedTemplate);

  const nextDisabledReason =
    step === 0 && !selectedTemplate
      ? t("certificates.create.selectTemplate")
      : step === 1 && missingForStep1.length > 0
        ? t("certificates.create.fillRequired", {
            fields: missingForStep1.join(", "),
          })
        : undefined;

  const handleNext = () => {
    if (!canGoNext) return;
    setStep((prev) => (prev < 3 ? ((prev + 1) as WizardStep) : prev));
  };

  const handlePrev = () => {
    setStep((prev) => (prev > 0 ? ((prev - 1) as WizardStep) : prev));
  };

  const renderStepper = () => (
    <div className={styles.stepper}>
      {steps.map((s, i) => {
        const state =
          i < step ? "completed" : i === step ? "active" : "pending";
        return (
          <React.Fragment key={s.label}>
            <div
              className={`${styles.stepItem} ${styles[`stepItem_${state}`]}`}
              onClick={() => {
                if (i < step) setStep(i as WizardStep);
              }}
            >
              <div className={styles.stepBadge}>
                {state === "completed" ? (
                  <Check width={16} height={16} color="#fff" />
                ) : (
                  <span>{i + 1}</span>
                )}
              </div>
              <div className={styles.stepText}>
                <span className={styles.stepLabel}>{s.label}</span>
                <span className={styles.stepDescription}>{s.description}</span>
              </div>
            </div>
            {i < steps.length - 1 && (
              <div
                className={`${styles.stepConnector} ${i < step ? styles.stepConnector_completed : ""}`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );

  /**
   * Tür seçimi ilk adımda duruyor: sertifikanın eğitim mi staj mı olduğu
   * sonraki her adımı (kime atanacağı, ne zaman üretileceği) belirliyor, bu
   * yüzden şablon seçilmeden önce sorulması gerekiyor.
   */
  const renderOwnerTypeField = () => (
    <section className={styles.formCard}>
      <div className={styles.formCardHeader}>
        <h3 className={styles.formCardTitle}>
          {t("certificates.create.fields.ownerTypeLabel")}
        </h3>
        <p className={styles.formCardSubtitle}>
          {ownerType === CertificateOwnerType.INTERN
            ? t("certificates.create.fields.ownerTypeHintIntern")
            : t("certificates.create.fields.ownerTypeHintEducation")}
        </p>
      </div>
      <div className={styles.formCardBody}>
        <Dropdown
          label={t("certificates.create.fields.ownerTypeLabel")}
          placeholder={t("certificates.create.fields.ownerTypePlaceholder")}
          options={ownerTypeOptions}
          value={String(ownerType)}
          selectedLabel={
            ownerTypeOptions.find((o) => o.value === String(ownerType))?.label
          }
          onSelect={handleOwnerTypeSelect}
          disabled={isEditMode}
        />
        {isEditMode && (
          <p className={styles.formCardSubtitle}>
            {t("certificates.edit.ownerTypeLocked")}
          </p>
        )}
      </div>
    </section>
  );

  const renderTemplateStep = () => (
    <div className={styles.stepPanel}>
      <input
        ref={fileInputRef}
        type="file"
        accept={TEMPLATE_FILE_ACCEPT}
        className={styles.hiddenFileInput}
        onChange={handleTemplateFileChange}
        disabled={uploadingTemplate}
        aria-hidden
      />

      {renderOwnerTypeField()}

      {templatesLoading ? (
        <div className={styles.statusBlock}>
          {t("common.loading")}
        </div>
      ) : templatesError ? (
        <div className={`${styles.statusBlock} ${styles.statusBlock_error}`}>
          {templatesError}
        </div>
      ) : (
        <>
          <div className={styles.templatesGrid}>
            <button
              type="button"
              className={styles.uploadCard}
              onClick={handleUploadTemplateClick}
              disabled={uploadingTemplate}
            >
              <div className={styles.uploadCardIcon}>
                <FileUpload width={32} height={32} />
              </div>
              <span className={styles.uploadCardTitle}>
                {uploadingTemplate
                  ? t("certificates.create.uploading")
                  : t("certificates.create.uploadButton")}
              </span>
              <span className={styles.uploadCardHint}>
                {t("certificates.create.uploadText")}
              </span>
            </button>

            {paginatedTemplates.map((template) => {
              const isSelected = selectedTemplate?.id === template.id;
              return (
                <div
                  key={template.id}
                  className={`${styles.templateCard} ${isSelected ? styles.templateCardSelected : ""}`}
                  onClick={() => handleTemplateSelect(template)}
                >
                  <div className={styles.templatePreview}>
                    <img
                      src={template.imagePath}
                      alt={template.name}
                      className={styles.templateImage}
                      crossOrigin="anonymous"
                    />
                    {isSelected && (
                      <div className={styles.templateSelectedBadge}>
                        <Check width={14} height={14} color="#fff" />
                      </div>
                    )}
                    <button
                      type="button"
                      className={styles.templateDeleteButton}
                      onClick={(e) => handleRequestDeleteTemplate(template, e)}
                      aria-label={t("certificates.create.deleteTemplate")}
                      title={t("certificates.create.deleteTemplate")}
                    >
                      <Delete width={16} height={16} />
                    </button>
                  </div>
                  <div className={styles.templateMeta}>
                    <span className={styles.templateName}>{template.name}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {totalRecords > 0 && (
            <div className={styles.paginationBar}>
              <div className={styles.paginationLeft}>
                <span className={styles.paginationLabel}>
                  {t("pagination.show")}
                </span>
                <div className={styles.pageSizeDropdown}>
                  <Dropdown
                    onSelect={(v) =>
                      handlePageSizeChange(Array.isArray(v) ? v[0] : v)
                    }
                    options={PAGE_SIZE_OPTIONS.map((size) => ({
                      label: size.toString(),
                      value: size.toString(),
                    }))}
                    menuPosition="top"
                    selectedLabel={pageSize.toString()}
                    value={pageSize.toString()}
                  />
                </div>
                <span className={styles.paginationDetails}>
                  {t("pagination.details", {
                    total: totalRecords,
                    start: Math.min(
                      (currentPage - 1) * pageSize + 1,
                      totalRecords,
                    ),
                    end: Math.min(currentPage * pageSize, totalRecords),
                  })}
                </span>
              </div>
              <div className={styles.paginationControls}>
                <button
                  type="button"
                  className={styles.paginationArrow}
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  <ArrowLeft height={16} width={16} />
                </button>
                {renderPaginationNumbers(
                  currentPage,
                  totalPages,
                  setCurrentPage,
                )}
                <button
                  type="button"
                  className={styles.paginationArrow}
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  <ArrowRight height={16} width={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );

  const renderInfoStep = () => (
    <div className={`${styles.stepPanel} ${styles.infoStep}`}>
      <div className={styles.infoForm}>
        {missingForStep1.length > 0 && (
          <div className={styles.requiredBanner}>
            <span className={styles.requiredBannerDot} />
            {t("certificates.create.fillRequired", {
              fields: missingForStep1.join(", "),
            })}
          </div>
        )}
        <section className={styles.formCard}>
          <div className={styles.formCardHeader}>
            <h3 className={styles.formCardTitle}>{t("certificates.create.certificateType")}</h3>
            <p className={styles.formCardSubtitle}>
              Bu sertifikayı listede tanıyabilmek için bir isim girin.
              Katılımcı adı, eğitim adı, tarih gibi bilgiler sertifika
              verildiğinde otomatik doldurulur.
            </p>
          </div>
          <div className={styles.formCardBody}>
            <Input
              label={t("certificates.create.fields.name")}
              placeholder={t("certificates.create.fields.namePlaceholder")}
              value={certificateName}
              onChange={(e) => setCertificateName(e.target.value)}
              required
              maxLength={255}
            />
            <div>
              <PlaceholderChips
                title={t("certificates.create.fields.descriptionTokensInfo")}
                placeholders={DESCRIPTION_PLACEHOLDERS}
                onInsert={insertIntoDescription}
              />
              <Textarea
                label={t("certificates.create.fields.descriptionLabel")}
                placeholder={t("certificates.create.fields.descriptionPlaceholder")}
                value={certificateDescription}
                onChange={(e) => setCertificateDescription(e.target.value)}
                rows={4}
                maxLength={1000}
              />
              <small className={styles.fieldHint}>
                Türkçe etiketler tıklandığında metne ilgili yer tutucu eklenir.
                Sertifika verildiğinde bu alanlar otomatik doldurulur.
              </small>
            </div>
          </div>
        </section>

        <section className={styles.formCard}>
          <div className={styles.formCardHeader}>
            <h3 className={styles.formCardTitle}>{t("certificates.create.numbering")}</h3>
            <p className={styles.formCardSubtitle}>
              Her sertifika bu formata göre otomatik numaralandırılır ve sıra
              numarası her yeni sertifikada artar.
            </p>
          </div>
          <div className={styles.formCardBody}>
            <PlaceholderChips
              title={t("certificates.create.fields.numberTokensInfo")}
              placeholders={NUMBER_PLACEHOLDERS}
              onInsert={insertIntoNumberPattern}
            />
            <Input
              label={t("certificates.create.fields.numberFormat")}
              placeholder={DEFAULT_NUMBER_PATTERN}
              value={numberPattern}
              onChange={(e) => setNumberPattern(e.target.value)}
              maxLength={200}
              error={!!numberPatternError}
              errorMessage={numberPatternError ?? undefined}
            />
            <div className={styles.patternPreview}>
              <span className={styles.patternPreviewLabel}>{t("certificates.create.steps.preview")}</span>
              <code className={styles.patternPreviewValue}>{numberPreview}</code>
              {numberPattern.trim() === "" && (
                <small className={styles.patternPreviewHint}>
                  Boş bırakılırsa varsayılan numara formatı kullanılır.
                </small>
              )}
            </div>
          </div>
        </section>
      </div>

      <aside className={styles.livePreview}>
        <div className={styles.livePreviewLabel}>
          Önizleme (örnek verilerle)
        </div>
        <div className={styles.livePreviewFrame}>
          <div ref={livePreviewRef} className={styles.livePreviewScale}>
            {effectiveTemplate && (
              <CertificatePreview
                template={effectiveTemplate}
                data={previewData}
                hiddenFields={hiddenFields}
              />
            )}
          </div>
        </div>
      </aside>
    </div>
  );

  const renderPositioningStep = () => (
    <div className={styles.stepPanel}>
      {effectiveTemplate && customPositions && (
        <CertificatePositionEditor
          template={effectiveTemplate}
          positions={customPositions}
          data={previewData}
          hiddenFields={hiddenFields}
          onChange={setCustomPositions}
          onToggleField={(key, visible) =>
            setHiddenFields((prev) =>
              visible ? prev.filter((k) => k !== key) : [...prev, key],
            )
          }
          onReset={() => {
            if (!selectedTemplate) return;
            setCustomPositions(
              JSON.parse(JSON.stringify(selectedTemplate.textPositions)),
            );
            // "Varsayılana Dön" tasarımın tamamını sıfırlar; kapatılan alanlar
            // da tasarımın parçası, aksi halde sıfırlanan sertifikada eksik
            // alan kalırdı.
            setHiddenFields([]);
          }}
        />
      )}
    </div>
  );

  const renderPreviewStep = () => (
    <div className={`${styles.stepPanel} ${styles.previewStep}`}>
      <div className={styles.finalPreviewWrapper}>
        {effectiveTemplate && (
          <CertificatePreview
            ref={previewRef}
            template={effectiveTemplate}
            data={previewData}
            hiddenFields={hiddenFields}
          />
        )}
      </div>

      <div className={styles.summaryCard}>
        <h4>{t("certificates.create.summary")}</h4>
        <dl>
          <div>
            <dt>{t("certificates.create.fields.name")}</dt>
            <dd>{certificateName || "—"}</dd>
          </div>
          <div>
            <dt>{t("common.description")}</dt>
            <dd>{certificateDescription || "—"}</dd>
          </div>
          <div>
            <dt>{t("certificates.create.fields.numberFormat")}</dt>
            <dd>
              <code>{effectivePattern}</code>
              {numberPattern.trim() === "" && (
                <small> {t("certificates.create.defaultSuffix")}</small>
              )}
            </dd>
          </div>
          <div>
            <dt>{t("certificates.create.firstNumberPreview")}</dt>
            <dd>
              <code>{numberPreview}</code>
            </dd>
          </div>
        </dl>
        <p className={styles.summaryHint}>
          Katılımcı, eğitim, eğitmen ve tarih alanları her sertifika
          verildiğinde otomatik olarak doldurulur.
        </p>
      </div>
    </div>
  );

  return (
    <DashboardLayout>
      <Header
        title={
          isEditMode
            ? t("certificates.edit.title")
            : t("certificates.create.title")
        }
      />

      <div className={styles.container}>
        <div className={styles.breadcrumbContainer}>
          <button
            type="button"
            className={styles.backButton}
            onClick={handleGoBack}
            aria-label={t("common.back")}
          >
            <LeftChevron width={20} height={20} />
          </button>
          <Breadcrumb items={breadcrumbItems} />
        </div>

        {renderStepper()}

        {step === 0 && renderTemplateStep()}
        {step === 1 && renderInfoStep()}
        {step === 2 && renderPositioningStep()}
        {step === 3 && renderPreviewStep()}

        <div className={styles.wizardFooter}>
          <Button
            variant="outlined"
            color="default"
            onClick={handlePrev}
            disabled={step === 0}
            startIcon={<ArrowLeft width={16} height={16} />}
          >
            {t("common.previous")}
          </Button>

          <div className={styles.wizardFooterRight}>
            {step < 3 && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleNext}
                disabled={!canGoNext}
                title={nextDisabledReason}
                endIcon={<ArrowRight width={16} height={16} color="#fff" />}
              >
                {t("common.continue")}
              </Button>
            )}
            {step === 3 && (
              <>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleGeneratePdf}
                  disabled={isGenerating || !selectedTemplate}
                >
                  {isGenerating
                    ? t("certificates.create.form.generating")
                    : t("certificates.create.form.downloadPdf")}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSave}
                  disabled={
                    savingCertificate ||
                    loadingCertificate ||
                    !certificateName.trim() ||
                    !!numberPatternError
                  }
                >
                  {savingCertificate
                    ? t("certificates.create.form.saving")
                    : isEditMode
                      ? t("certificates.edit.form.save")
                      : t("certificates.create.form.save")}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Hidden full-size preview for PDF/HTML generation when on other steps */}
      {step !== 3 && effectiveTemplate && (
        <div className={styles.hiddenRenderRoot} aria-hidden>
          <CertificatePreview
            ref={previewRef}
            template={effectiveTemplate}
            data={previewData}
            hiddenFields={hiddenFields}
          />
        </div>
      )}

      <ConfirmModal
        isOpen={!!templateToDelete}
        onClose={() => !deletingTemplate && setTemplateToDelete(null)}
        onConfirm={handleConfirmDeleteTemplate}
        title={t("certificates.create.deleteConfirmTitle")}
        message={t("certificates.create.deleteConfirmMessage")}
        confirmText={t("common.delete")}
        cancelText={t("common.cancel")}
        confirmVariant="danger"
        loading={deletingTemplate}
      />
    </DashboardLayout>
  );
};

export default CreateCertificatePage;
